<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click="closeModal">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden" @click.stop>
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">{{ t('kanban.scheduler.title') }}</h2>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <div class="flex h-[calc(90vh-80px)]">
        <!-- Left Panel - Calendar -->
        <div class="flex-1 p-6">
          <!-- Selected Date Info -->
          <div class="mb-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
            <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">{{ t('kanban.scheduler.selectedDate') }}</h3>
            <p class="text-2xl font-bold text-blue-600 dark:text-blue-400">{{ formatSelectedDate(selectedDate) }}</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('kanban.scheduler.appointmentsCount', { count: getAppointmentCount(selectedDate) }) }}</p>
          </div>

          <!-- Calendar -->
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <!-- Calendar Header -->
            <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <button @click="previousMonth" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ formatMonthYear(currentDate) }}</h3>
              <button @click="nextMonth" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>

            <!-- Calendar Grid -->
            <div class="p-4">
              <!-- Days of week -->
              <div class="grid grid-cols-7 gap-1 mb-2">
                <div v-for="day in daysOfWeek" :key="day" class="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2">
                  {{ day }}
                </div>
              </div>

              <!-- Calendar days -->
              <div class="grid grid-cols-7 gap-1">
                <button
                  v-for="day in calendarDays"
                  :key="`${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`"
                  @click="selectDate(day.date)"
                  :class="[
                    'p-2 text-sm rounded-lg transition-colors relative',
                    day.isCurrentMonth 
                      ? 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700' 
                      : 'text-gray-400 dark:text-gray-500',
                    isSelectedDate(day.date) 
                      ? 'bg-blue-500 text-white hover:bg-blue-600' 
                      : '',
                    day.hasAppointments 
                      ? 'bg-blue-50 dark:bg-blue-900' 
                      : ''
                  ]"
                >
                  {{ day.date.getDate() }}
                  <div v-if="day.hasAppointments" class="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                </button>
              </div>
            </div>

            <!-- Legend -->
            <div class="p-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <div class="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span>{{ t('kanban.scheduler.legend') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel - Time Selection -->
        <div class="w-80 border-l border-gray-200 dark:border-gray-700 p-6">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">{{ t('kanban.scheduler.availableTimes') }}</h3>
          
          <div class="space-y-2 max-h-96 overflow-y-auto">
            <button
              v-for="timeSlot in availableTimeSlots"
              :key="timeSlot"
              @click="selectTime(timeSlot)"
              :class="[
                'w-full p-3 text-left rounded-lg border transition-colors',
                selectedTime === timeSlot
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
              ]"
            >
              {{ timeSlot }}
            </button>
          </div>

          <!-- Selected Time Info -->
          <div v-if="selectedTime" class="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 class="font-medium text-gray-900 dark:text-white mb-2">{{ t('kanban.scheduler.selectedAppointment') }}</h4>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ t('kanban.scheduler.appointmentAt', { date: formatSelectedDate(selectedDate), time: selectedTime }) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <button @click="closeModal" class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
          {{ t('common.cancel') }}
        </button>
        <button @click="confirmAppointment" :disabled="!selectedTime" class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {{ t('kanban.scheduler.confirmAppointment') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const props = defineProps({
  isOpen: Boolean,
  initialDate: Date
})

const emit = defineEmits(['close', 'confirm'])

// Calendar state
const currentDate = ref(new Date())
const selectedDate = ref(props.initialDate || new Date())
const selectedTime = ref('')

// Available time slots (7:00 to 18:30, 30-minute intervals)
const availableTimeSlots = ref([
  '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30'
])

// Days of week
const daysOfWeek = computed(() => [
  t('kanban.scheduler.weekdays.sun'),
  t('kanban.scheduler.weekdays.mon'),
  t('kanban.scheduler.weekdays.tue'),
  t('kanban.scheduler.weekdays.wed'),
  t('kanban.scheduler.weekdays.thu'),
  t('kanban.scheduler.weekdays.fri'),
  t('kanban.scheduler.weekdays.sat')
])

// Mock data for appointments (in real app, this would come from API)
const appointments = ref([
  { date: new Date(2025, 9, 2), time: '10:00' },
  { date: new Date(2025, 9, 2), time: '14:30' },
  { date: new Date(2025, 9, 16), time: '09:00' },
  { date: new Date(2025, 9, 16), time: '15:00' },
  { date: new Date(2025, 9, 20), time: '11:30' }
])

// Computed properties
const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())
  
  const days = []
  const current = new Date(startDate)
  
  // Generate 42 days (6 weeks)
  for (let i = 0; i < 42; i++) {
    const isCurrentMonth = current.getMonth() === month
    const hasAppointments = appointments.value.some(apt => 
      apt.date.getDate() === current.getDate() && 
      apt.date.getMonth() === current.getMonth() && 
      apt.date.getFullYear() === current.getFullYear()
    )
    
    days.push({
      date: new Date(current),
      isCurrentMonth,
      hasAppointments
    })
    
    current.setDate(current.getDate() + 1)
  }
  
  return days
})

// Methods
const closeModal = () => {
  emit('close')
}

const selectDate = (date) => {
  selectedDate.value = date
  selectedTime.value = '' // Reset time when date changes
}

const selectTime = (time) => {
  selectedTime.value = time
}

const previousMonth = () => {
  currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1)
}

const nextMonth = () => {
  currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1)
}

const isSelectedDate = (date) => {
  return selectedDate.value && 
    date.getDate() === selectedDate.value.getDate() &&
    date.getMonth() === selectedDate.value.getMonth() &&
    date.getFullYear() === selectedDate.value.getFullYear()
}

const getAppointmentCount = (date) => {
  if (!date) return 0
  return appointments.value.filter(apt => 
    apt.date.getDate() === date.getDate() && 
    apt.date.getMonth() === date.getMonth() && 
    apt.date.getFullYear() === date.getFullYear()
  ).length
}

const formatSelectedDate = (date) => {
  if (!date) return ''
  return date.toLocaleDateString(dateLocale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

const formatMonthYear = (date) => {
  return date.toLocaleDateString(dateLocale.value, {
    month: 'long',
    year: 'numeric'
  })
}

const confirmAppointment = () => {
  if (selectedDate.value && selectedTime.value) {
    const appointment = {
      date: selectedDate.value,
      time: selectedTime.value
    }
    emit('confirm', appointment)
    closeModal()
  }
}

// Watch for initial date changes
watch(() => props.initialDate, (newDate) => {
  if (newDate) {
    selectedDate.value = newDate
    currentDate.value = new Date(newDate.getFullYear(), newDate.getMonth(), 1)
  }
}, { immediate: true })
</script>

<style scoped>
/* Custom scrollbar for time slots */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
